import { Badge } from '__generated__/athena/graphql';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { isBuyDirectEligible } from 'components/marketplace/helpers/buyDirectHelpers';
import {
  determinePropertyActionability,
  type PropertyActionabilityInput,
} from 'components/marketplace/helpers/propertyActionability';
import type { MarketplaceProperty } from 'components/marketplace/property/types';

import { COSMOS_URL } from '../../globals';
import { FALLBACK_URL } from '../components/CoverImages';

function getHumanReadableAddress(
  street?: string | null,
  unitType?: string | null,
  unitNumber?: string | null,
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
) {
  const formattedStreet = formatStreetAddress(street, unitType, unitNumber);
  if (!formattedStreet || !city || !state || !postalCode) {
    return '';
  }
  return `${formattedStreet}, ${city}, ${state} ${postalCode}`;
}

const SOLD_OR_UNAVAILABLE_STATUSES = new Set([
  'RECENTLY_SOLD',
  'IN_CONTRACT',
  'AVAILABLE_SOON',
  'TEMP_UNAVAILABLE',
]);

const COMING_SOON_STATUSES = new Set(['AVAILABLE_SOON']);

export type SeoListingFraming = 'for-sale' | 'sold' | 'coming-soon';

// Active for-sale states are ON_THE_MARKET and TRADITIONAL (an MLS listing that
// still requires an agent). Sold, under contract, coming soon, and temporarily
// unavailable homes are not purchasable from the PDP, so they share this set
// for offer schema and purchase UX. Title copy is a separate mapping — coming
// soon uses its own framing. UNKNOWN is intentionally excluded here because
// its actionability depends on `canSelfTour`; see
// `isSoldOrUnavailableProperty` for the actionability-aware classification.
export const isSoldOrUnavailableStatus = (status: string | null | undefined): boolean =>
  !!status && SOLD_OR_UNAVAILABLE_STATUSES.has(status);

export const isComingSoonStatus = (status: string | null | undefined): boolean =>
  !!status && COMING_SOON_STATUSES.has(status);

// Coming-soon homes are not purchasable, but they are upcoming inventory, not
// sold. Check listingState first and displayBadge second so this matches the
// visible status pill.
export const isComingSoonProperty = (property: PropertyActionabilityInput): boolean =>
  isComingSoonStatus(property.listingState) || isComingSoonStatus(property.displayBadge);

// SEO framing should match whether a buyer can actually act on the PDP (tour or
// buy). `listingState` carries the sold/contract/coming-soon states while
// `displayBadge` carries the badge-only TEMP_UNAVAILABLE state (whose
// listingState stays ON_THE_MARKET), so a sold/unavailable signal can come from
// either field — check both rather than picking one via a precedence rule. This
// mirrors the visible status pill, which resolves a label from listingState
// first and falls back to displayBadge, so SEO and the pill read the same
// status sources. UNKNOWN isn't in the sold/unavailable set; its framing depends
// on `canSelfTour`, so delegate to `determinePropertyActionability`, which keeps
// an actionable UNKNOWN home (tour-with-agent) on "for sale" SEO while still
// treating non-actionable UNKNOWN (and sold/under-contract/coming-soon/
// unavailable) homes as not for sale.
export const isSoldOrUnavailableProperty = (property: PropertyActionabilityInput): boolean => {
  if (isSoldOrUnavailableStatus(property.listingState)) {
    return true;
  }
  if (isSoldOrUnavailableStatus(property.displayBadge)) {
    return true;
  }
  const hasUnknownStatus = property.displayBadge === Badge.UNKNOWN;
  if (hasUnknownStatus) {
    return !determinePropertyActionability(property).isActionable;
  }
  return false;
};

// Title copy is more specific than "not for sale": coming soon must not inherit
// the sold title. Check coming soon first because AVAILABLE_SOON is also in
// the sold/unavailable set used for schema and purchase UX.
export const getSeoListingFraming = (property: PropertyActionabilityInput): SeoListingFraming => {
  if (isComingSoonProperty(property)) {
    return 'coming-soon';
  }
  if (isSoldOrUnavailableProperty(property)) {
    return 'sold';
  }
  return 'for-sale';
};

// Whether for-sale/purchase UX (e.g. the estimated monthly payment) should
// appear for this home. The home must be actionable (tour/buy-eligible per
// `determinePropertyActionability`, which reads `displayBadge`) AND not
// sold/unavailable (`isSoldOrUnavailableProperty`, which reads both
// `listingState` and `displayBadge`). Combining both keeps the monthly payment
// aligned with SEO and the status pill: a divergent sold `listingState` (e.g.
// RECENTLY_SOLD) paired with an actionable `displayBadge` (e.g. ON_THE_MARKET)
// would otherwise show a payment on a home framed as sold. This is a strict
// refinement of the old `isActionable`-only gate, so it can only suppress the
// payment, never surface it where it wasn't shown before.
export const isPropertyAvailableForPurchase = (property: PropertyActionabilityInput): boolean =>
  determinePropertyActionability(property).isActionable && !isSoldOrUnavailableProperty(property);

// Returns the price to emit as the schema.org Offer `price`, or null when no
// Offer should be emitted. Sold/unavailable homes return null so crawlers don't
// see an InStock offer for a home that isn't for sale; active homes emit their
// BuyDirect price (when eligible) or list price.
type PropertyOfferInput = PropertyActionabilityInput & Pick<MarketplaceProperty, 'listPrice'>;

export const getSchemaOfferPrice = (property: PropertyOfferInput): number | null => {
  if (isSoldOrUnavailableProperty(property)) {
    return null;
  }
  if (isBuyDirectEligible(property)) {
    return property.buyDirectPrice ?? null;
  }
  return property.listPrice ?? null;
};

const SEO_TITLE_PREFIX: Record<SeoListingFraming, string> = {
  'for-sale': 'Home for sale',
  sold: 'Sold home',
  'coming-soon': 'Coming soon',
};

interface GetSeoFieldsParams {
  addressId: string | undefined;
  displaySlug: string | null | undefined;
  framing: SeoListingFraming;
  street: string | undefined | null;
  unitType: string | undefined | null;
  unitNumber: string | undefined | null;
  city: string | undefined | null;
  state: string | undefined | null;
  postalCode: string | undefined | null;
  bedrooms: number | null | undefined;
  bathrooms: number | null | undefined;
  sqFtTotalLiving: number | null | undefined;
  coverImageProp: string | null | undefined;
}

export const getSeoFields = ({
  addressId,
  displaySlug,
  framing,
  street,
  unitType,
  unitNumber,
  city,
  state,
  postalCode,
  bedrooms,
  bathrooms,
  sqFtTotalLiving,
  coverImageProp,
}: GetSeoFieldsParams) => {
  const readableAddress = getHumanReadableAddress(
    street,
    unitType,
    unitNumber,
    city,
    state,
    postalCode,
  );
  const title = `${SEO_TITLE_PREFIX[framing]} - ${readableAddress} | Opendoor`;
  const canonicalUrl = displaySlug
    ? `${COSMOS_URL}/properties/${displaySlug}/aid_${addressId}`
    : `${COSMOS_URL}/properties/${street ? `${street?.toLowerCase()?.replaceAll(' ', '-')}-` : ''}${
        unitNumber ? `${unitNumber}-` : ''
      }${
        city ? `${city?.toLowerCase()}-` : ''
      }-${state?.toLowerCase()}${postalCode}/aid_${addressId}`;
  // Build the facts fragment from present attributes only so a listing missing
  // bedrooms/bathrooms/sqft doesn't publish "null bedroom" / "undefined sqft"
  // SEO and social-preview copy.
  const facts: string[] = [];
  if (bedrooms != null) {
    facts.push(`${bedrooms} bedroom`);
  }
  if (bathrooms != null) {
    facts.push(`${bathrooms} bathroom`);
  }
  if (sqFtTotalLiving != null) {
    facts.push(`${sqFtTotalLiving} sqft`);
  }
  const homeDescription = facts.length ? `this ${facts.join(', ')} home` : 'this home';
  const metaDescription =
    framing === 'for-sale'
      ? `View photos, details, and schedule a self-tour of ${homeDescription} located at ${readableAddress}.`
      : `View photos and details of ${homeDescription} located at ${readableAddress}.`;
  let coverImage = FALLBACK_URL;
  if (coverImageProp) {
    // Use a larger preset so link previews (iMessage/SMS, Facebook, Twitter, WhatsApp)
    // render a high-quality hero image rather than a tiny thumbnail.
    coverImage = `${coverImageProp}?${new URLSearchParams({
      preset: '3:2-lg',
      service: 'cosmos',
    }).toString()}`;
  }
  return { readableAddress, canonicalUrl, metaDescription, title, coverImage };
};
